import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useContent } from '../../contexts/ContentContext';
import { useTranslation } from '../../i18n';
import useWeddingSlug from '../../hooks/useWeddingSlug';
import BottomFloatingMenu from '../../components/ui/BottomFloatingMenu';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const GuestGallery = () => {
  const navigate = useNavigate();
  const { weddingPath } = useWeddingSlug();
  const { content } = useContent();
  const { t } = useTranslation();

  // Read gallery URL from CMS (fallback to settings for backward compatibility)
  const galleryUrl = content?.gallery?.publicGalleryUrl ||
    content?.settings?.googlePhotosAlbumUrl ||
    'https://photos.google.com/share/YOUR_ALBUM_SHARE_LINK';

  const galleryTitle = content?.gallery?.galleryTitle || 'Wedding Gallery';

  const handleUploadPhotos = () => {
    window.open(galleryUrl, '_blank', 'noopener,noreferrer');
  };

  const handleViewGallery = () => {
    window.open(galleryUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Helmet>
        <title>Wedding Gallery - Share Your Photos</title>
        <meta name="description" content="Share your photos from our special day" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-background to-muted pb-24">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-40">
          <div className="max-w-screen-xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(weddingPath())}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="ArrowLeft" size={20} />
                <span>{t('nav.back')}</span>
              </button>
              <h1 className="text-xl font-bold">{galleryTitle}</h1>
              <div className="w-16" /> {/* Spacer for alignment */}
            </div>
          </div>
        </div>

        <div className="max-w-screen-xl mx-auto px-4 py-8">
          {/* Welcome Section */}
          <div className="bg-white rounded-lg border p-8 mb-6 text-center">
            <Icon name="Camera" size={64} className="mx-auto mb-4 text-primary" />
            <h2 className="text-3xl font-bold mb-3">{t('gallery.shareMemories')}</h2>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              {t('gallery.shareDescription')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                onClick={handleUploadPhotos}
                iconName="Upload"
                iconPosition="left"
                className="min-w-[200px]"
              >
                {t('gallery.uploadPhotos')}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleViewGallery}
                iconName="Images"
                iconPosition="left"
                className="min-w-[200px]"
              >
                {t('gallery.viewGallery')}
              </Button>
            </div>
          </div>

          {/* Instructions Section */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Icon name="Info" size={24} className="text-primary" />
              {t('gallery.howToUpload')}
            </h3>
            <ol className="space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center font-medium">1</span>
                <span dangerouslySetInnerHTML={{ __html: t('gallery.step1') }} />
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center font-medium">2</span>
                <span dangerouslySetInnerHTML={{ __html: t('gallery.step2') }} />
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center font-medium">3</span>
                <span dangerouslySetInnerHTML={{ __html: t('gallery.step3') }} />
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center font-medium">4</span>
                <span dangerouslySetInnerHTML={{ __html: t('gallery.step4') }} />
              </li>
            </ol>

            <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
              <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: t('gallery.privacyNote') }} />
            </div>
          </div>

          {/* Thank You Section */}
          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              {t('gallery.thankYou')}
            </p>
          </div>
        </div>

        <BottomFloatingMenu />
      </div>
    </>
  );
};

export default GuestGallery;
